import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { getGastosFijos, calcularCostoNetoReal, categoriaLabel } from '../../lib/contabilidadApi'
import { useAuth } from '../../lib/AuthContext'
import VencimientosFiscalesSection from '../../components/VencimientosFiscalesSection'

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')

// Default export: la página standalone /admin/finanzas (compat URL antigua).
// El hub /admin/finanzas/reportes importa <ContabilidadResumen /> y lo usa como tab.
export default function Contabilidad() {
  return <AdminLayout><ContabilidadResumen /></AdminLayout>
}

export function ContabilidadResumen() {
  const { isSuperadmin } = useAuth()
  const [gastos, setGastos] = useState([])
  const [loading, setLoading] = useState(true)
  const [periodo, setPeriodo] = useState(() => new Date().toISOString().slice(0, 7))
  const [descargando, setDescargando] = useState(false)
  const [exportMsg, setExportMsg] = useState(null)

  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true)
    setGastos(await getGastosFijos({ soloActivos: true }))
    setLoading(false)
  }

  async function descargarLibroDiario({ ejemplo = false } = {}) {
    setDescargando(true); setExportMsg(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/export-libro-diario-zeta`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ periodo, ejemplo }),
      })
      const ct = resp.headers.get('content-type') || ''
      if (ct.includes('spreadsheetml') || ct.includes('xlsx')) {
        const buf = await resp.arrayBuffer()
        const asientos = resp.headers.get('X-Asientos')
        const lineas = resp.headers.get('X-Lineas')
        const modo = resp.headers.get('X-Modo')
        const sufijo = modo === 'ejemplo' ? ' EJEMPLO' : ''
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `Limpack Asientos ${periodo}${sufijo}.xlsx`
        document.body.appendChild(a); a.click(); a.remove()
        setExportMsg({
          tipo: 'ok',
          txt: modo === 'ejemplo'
            ? `Ejemplo XLSX descargado: ${asientos} asientos · ${lineas} líneas. Formato Zeta exacto (17 columnas, tipos correctos). Subilo a Zeta para validar.`
            : `XLSX descargado: ${asientos} asientos · ${lineas} líneas del período ${periodo}. Formato Zeta listo para importar.`
        })
      } else {
        const j = await resp.json()
        if (j.ok && j.sugerencia_ejemplo) {
          setExportMsg({
            tipo: 'warn',
            txt: 'No hay asientos cargados todavía en este período. Para validar el formato con Roberto, descargá el CSV de ejemplo →',
            sugerirEjemplo: true,
          })
        } else if (j.ok) {
          setExportMsg({ tipo: 'warn', txt: j.nota || `Sin asientos en ${periodo}.` })
        } else {
          setExportMsg({ tipo: 'error', txt: j.error || 'Error generando libro diario' })
        }
      }
    } catch (e) {
      setExportMsg({ tipo: 'error', txt: e.message })
    } finally {
      setDescargando(false)
    }
  }

  // Totales del mes
  let totalConIva = 0, totalDeducible = 0, totalCreditoIva = 0
  const porCategoria = {}
  for (const g of gastos) {
    const costo = calcularCostoNetoReal(g)
    totalConIva += costo
    if (g.es_deducible_irae) totalDeducible += costo
    if (g.tiene_credito_fiscal_iva) {
      totalCreditoIva += (Number(g.monto_neto) - Number(g.monto_descuento || 0)) * (Number(g.tasa_iva) / 100)
    }
    porCategoria[g.categoria_contable] = (porCategoria[g.categoria_contable] || 0) + costo
  }
  const categoriasOrdenadas = Object.entries(porCategoria).sort((a, b) => b[1] - a[1])

  return (
    <>
      <div className="cb-wrap" style={{ padding: 0 }}>
        {/* Banner Zeta: selector de mes + descarga real del libro diario */}
        <div className="cb-banner cb-banner-ok" style={{ marginTop: 0 }}>
          <span style={{ fontSize: 22 }}>📒</span>
          <div style={{ flex: 1 }}>
            <strong>Sincronización con Zeta: XLSX mensual.</strong>
            <span>Formato Zeta exacto (17 columnas, tipos por columna). Roberto importa a Zeta → Contabilidad → Importar Asientos.</span>
          </div>
          <input
            type="month"
            value={periodo}
            onChange={e => setPeriodo(e.target.value)}
            style={{ padding: '6px 9px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', color: '#1A3461' }}
          />
          <button
            className="cb-btn-zeta-ghost"
            onClick={() => descargarLibroDiario({ ejemplo: true })}
            disabled={descargando}
            title="CSV sintético con los 5 casos Roberto (compra mercadería, packing, venta contado, Handy, descuento) + anticipo IRAE. No toca DB."
          >
            📄 Ejemplo
          </button>
          <button className="cb-btn-zeta" onClick={() => descargarLibroDiario()} disabled={descargando}>
            {descargando ? '⏳ Generando…' : '⬇ Descargar libro diario'}
          </button>
        </div>
        {exportMsg && (
          <div className={`cb-export-msg cb-export-${exportMsg.tipo}`}>
            {exportMsg.txt}
            {exportMsg.sugerirEjemplo && (
              <button
                style={{ marginLeft: 8, padding: '4px 12px', background: '#92400e', color: '#fff', border: 0, borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
                onClick={() => descargarLibroDiario({ ejemplo: true })}
                disabled={descargando}
              >
                📄 Descargar ejemplo
              </button>
            )}
          </div>
        )}

        {/* KPIs principales */}
        {loading ? (
          <div className="cb-loading">Cargando…</div>
        ) : (
          <>
            <div className="cb-kpis">
              <KpiBig label="Gastos fijos mensuales" value={fmt(totalConIva)} sub={`${gastos.length} conceptos · c/IVA neto de descuentos`} color="#dc2626" />
              <KpiBig label="Deducible de IRAE" value={fmt(totalDeducible)} sub="baja base imponible IRAE 25%" color="#16a34a" />
              <KpiBig label="Crédito fiscal IVA" value={fmt(totalCreditoIva)} sub="descontable de IVA débito ventas" color="#2563eb" />
              <KpiBig label="Ahorro IRAE estimado" value={fmt(totalDeducible * 0.25)} sub="25% del deducible" color="#7c3aed" />
            </div>

            {/* Desglose por categoría */}
            <h2 className="cb-h2">Desglose por categoría</h2>
            <div className="cb-cat-tabla">
              {categoriasOrdenadas.map(([cat, monto]) => {
                const pct = totalConIva > 0 ? (monto / totalConIva) * 100 : 0
                return (
                  <div className="cb-cat-row" key={cat}>
                    <div className="cb-cat-label">{categoriaLabel(cat)}</div>
                    <div className="cb-cat-bar">
                      <div className="cb-cat-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="cb-cat-pct">{pct.toFixed(0)}%</div>
                    <div className="cb-cat-monto">{fmt(monto)}</div>
                  </div>
                )
              })}
              {categoriasOrdenadas.length === 0 && (
                <div className="cb-empty">Sin gastos cargados. <Link to="/admin/finanzas/gastos-fijos">Cargar gastos →</Link></div>
              )}
            </div>

            {/* Accesos rápidos */}
            <h2 className="cb-h2">Accesos rápidos</h2>
            <div className="cb-accesos">
              <AccesoCard
                to="/admin/finanzas/gastos-fijos"
                icon="💸"
                title="Gastos fijos mensuales"
                desc="Editar montos, IVA, categorías. Migración del modelo financiero."
              />
              <AccesoCard
                to="/admin/finanzas/plan-cuentas"
                icon="📒"
                title="Plan de cuentas"
                desc="Estructura PyME uruguaya. Editable según necesidad."
              />
              <AccesoCard
                to="/admin/cobros"
                icon="💰"
                title="Cobros B2C"
                desc="Cobros procesados con Handy."
              />
              <AccesoCard
                to="/admin/cobros-b2b"
                icon="🏦"
                title="Cobros B2B"
                desc="Transferencias bancarias de empresas."
              />
              <AccesoCard
                to="/admin/facturacion"
                icon="📤"
                title="Facturación emitida"
                desc="e-Tickets y e-Facturas vía Biller."
              />
              <AccesoCard
                to="/admin/compras"
                icon="🧾"
                title="Compras (e-Facturas)"
                desc="e-Facturas recibidas de proveedores."
              />
              <AccesoCard
                to="/admin/reportes/iva"
                icon="🧮"
                title="IVA mensual — cuadre"
                desc="Cuadre IVA débito vs crédito (vista operativa)."
              />
              <AccesoCard
                to="/admin/finanzas/reportes?tab=dgi"
                icon="📑"
                title="Declaraciones DGI"
                desc="Form 1387, Anticipo IRAE, Form 2181 NO CEDE."
              />
              <AccesoCard
                to="/admin/reportes/modelo-financiero"
                icon="📈"
                title="Modelo financiero"
                desc="Proyección 12M + KPIs operativos."
              />
            </div>

            {/* Vencimientos fiscales — solo superadmin (Roberto) */}
            {isSuperadmin && <VencimientosFiscalesSection mode="full" />}
          </>
        )}
      </div>
      <style>{CSS}</style>
    </>
  )
}

function KpiBig({ label, value, sub, color = '#1A3461' }) {
  return (
    <div className="cb-kpi-big">
      <div className="cb-kpi-label">{label}</div>
      <div className="cb-kpi-val" style={{ color }}>{value}</div>
      {sub && <div className="cb-kpi-sub">{sub}</div>}
    </div>
  )
}

function AccesoCard({ to, icon, title, desc }) {
  return (
    <Link to={to} className="cb-acceso">
      <span className="cb-acceso-icon">{icon}</span>
      <div>
        <div className="cb-acceso-title">{title}</div>
        <div className="cb-acceso-desc">{desc}</div>
      </div>
      <span className="cb-acceso-arrow">›</span>
    </Link>
  )
}

export const CSS = `
.cb-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.cb-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 16px }
.cb-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.cb-headline p { font-size: 13px; color: #64748b; margin: 0 }

.cb-actions { display: flex; gap: 8px; flex-wrap: wrap }
.cb-btn-secondary { padding: 8px 14px; background: #fff; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; font-weight: 700; color: #475569; cursor: pointer; font-family: inherit }
.cb-btn-secondary:disabled { opacity: .5; cursor: not-allowed }
.cb-btn-zeta { padding: 8px 14px; background: linear-gradient(135deg, #1A3461, #0f2147); border: 0; border-radius: 8px; font-size: 13px; font-weight: 800; color: #fff; cursor: pointer; font-family: inherit }
.cb-btn-zeta:hover { background: linear-gradient(135deg, #0f2147, #08152b) }
.cb-btn-zeta:disabled { background: #94a3b8; cursor: not-allowed }
.cb-btn-zeta-ghost { padding: 8px 12px; background: #fff; border: 1px solid #14532d; border-radius: 8px; font-size: 13px; font-weight: 700; color: #166534; cursor: pointer; font-family: inherit }
.cb-btn-zeta-ghost:hover { background: #f0fdf4 }
.cb-btn-zeta-ghost:disabled { opacity: 0.5; cursor: not-allowed }

.cb-banner { display: flex; gap: 14px; align-items: flex-start; padding: 14px 18px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 18px }
.cb-banner strong { display: block; color: #78350f; font-size: 14px; margin-bottom: 4px }
.cb-banner span { font-size: 12.5px; color: #92400e; line-height: 1.5 }
.cb-banner code { background: rgba(120, 53, 15, 0.10); padding: 0 5px; border-radius: 4px; font-size: 11px }
.cb-banner-ok { background: #dcfce7; border-color: #86efac }
.cb-banner-ok strong { color: #14532d }
.cb-banner-ok span { color: #166534 }
.cb-banner-ok code { background: rgba(20, 83, 45, 0.10) }

.cb-export-msg { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 14px; font-weight: 600 }
.cb-export-ok { background: #dcfce7; border: 1px solid #86efac; color: #166534 }
.cb-export-warn { background: #fef3c7; border: 1px solid #fde68a; color: #92400e }
.cb-export-error { background: #fee2e2; border: 1px solid #fca5a5; color: #991b1b }

.cb-loading { padding: 60px; text-align: center; color: #94a3b8 }

.cb-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 24px }
.cb-kpi-big { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 18px }
.cb-kpi-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.cb-kpi-val { font-size: 28px; font-weight: 900; margin-top: 4px; letter-spacing: -.02em }
.cb-kpi-sub { font-size: 11.5px; color: #64748b; margin-top: 4px }

.cb-h2 { font-size: 16px; font-weight: 900; color: #1A3461; margin: 24px 0 12px }

.cb-cat-tabla { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 18px }
.cb-cat-row { display: grid; grid-template-columns: 200px 1fr 50px 110px; gap: 14px; align-items: center; padding: 8px 0; border-bottom: 1px solid #f1f5f9 }
.cb-cat-row:last-child { border-bottom: 0 }
.cb-cat-label { font-size: 13px; font-weight: 700; color: #1A3461 }
.cb-cat-bar { height: 8px; background: #f1f5f9; border-radius: 999px; overflow: hidden }
.cb-cat-bar-fill { height: 100%; background: linear-gradient(90deg, #00A8B5, #1A3461); border-radius: 999px }
.cb-cat-pct { text-align: right; font-size: 12px; color: #64748b; font-weight: 700 }
.cb-cat-monto { text-align: right; font-size: 14px; font-weight: 800; color: #1A3461 }
.cb-empty { padding: 18px; text-align: center; color: #94a3b8 }
.cb-empty a { color: #00A8B5; text-decoration: none; font-weight: 700 }

.cb-accesos { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px }
.cb-acceso { display: flex; gap: 12px; align-items: center; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: border-color .15s, transform .15s }
.cb-acceso:hover { border-color: #00A8B5; transform: translateY(-1px) }
.cb-acceso-icon { font-size: 24px; flex-shrink: 0 }
.cb-acceso-title { font-size: 13.5px; font-weight: 800; color: #1A3461 }
.cb-acceso-desc { font-size: 11.5px; color: #64748b; margin-top: 2px; line-height: 1.4 }
.cb-acceso-arrow { color: #cbd5e1; font-size: 22px; margin-left: auto }

@media (max-width: 720px) {
  .cb-cat-row { grid-template-columns: 1fr 50px 100px; gap: 8px }
  .cb-cat-bar { display: none }
}
`
